import { useCallback, useMemo } from 'react';

import { FormElementKind, ruleHasDeletedValue, type FormElement } from '@gearbelt/global';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';

import { getListOptionsQueryOptions } from '@/queries/lists';
import { useIsAWA } from '@/utils/awa';

type DropdownElement = Extract<FormElement, { kind: typeof FormElementKind.Dropdown }>;

interface DeletedLogicValues {
  /** True when any rule references a value absent from its trigger's current options. */
  hasDeletedValues: boolean;
  /** Rule ids whose values include at least one option that no longer exists. */
  deletedByRuleId: Set<string>;
  /** True while a list-sourced trigger's options are still loading — save must wait, then retry. */
  isResolving: boolean;
  /** True when a list's options failed to load — the rule can't be verified, so save is blocked terminally. */
  hasUnverifiableList: boolean;
}

function isDropdownTrigger(element: FormElement): element is DropdownElement {
  return element.kind === FormElementKind.Dropdown && (element.logicRules?.length ?? 0) > 0;
}

/**
 * Flags conditional-logic rules whose trigger value no longer exists in the source list. A list still loading
 * surfaces as `isResolving` (retry helps); one that errored or has no list configured surfaces as
 * `hasUnverifiableList` (retry won't help — the rule must be edited). The distinction drives the save-gate copy.
 */
export function useDeletedLogicValues(elements: FormElement[]): DeletedLogicValues {
  const { isAWA } = useIsAWA();

  const triggers = useMemo(() => elements.filter(isDropdownTrigger), [elements]);

  const listIds = useMemo(
    () => Array.from(new Set(triggers.flatMap((trigger) => (trigger.config.source === 'list' && trigger.config.listId ? [trigger.config.listId] : [])))),
    [triggers],
  );

  // TanStack re-runs combine — and returns a fresh result — only when a query result actually changes, so the
  // classification lives here rather than in a second memo that would depend on an unstable queries array.
  const combine = useCallback(
    (queries: UseQueryResult<string[]>[]): Omit<DeletedLogicValues, 'hasDeletedValues'> => {
      const optionsByListId = new Map<string, { values: Set<string>; loaded: boolean; errored: boolean }>();
      listIds.forEach((listId, index) => {
        const query = queries[index];
        optionsByListId.set(listId, { values: new Set(query?.data ?? []), loaded: query?.isSuccess ?? false, errored: query?.isError ?? false });
      });

      const deletedByRuleId = new Set<string>();
      let isResolving = false;
      let hasUnverifiableList = false;

      for (const trigger of triggers) {
        let currentValues: Set<string>;
        if (trigger.config.source === 'static') {
          currentValues = new Set(trigger.config.options);
        } else {
          // No list configured, or its fetch failed — the values can't be verified, so block save terminally
          // (editing the rule is the fix) rather than falling through to the clean path and false-passing it.
          if (!trigger.config.listId) {
            hasUnverifiableList = true;
            continue;
          }
          const entry = optionsByListId.get(trigger.config.listId);
          if (entry?.errored) {
            hasUnverifiableList = true;
            continue;
          }
          if (!entry?.loaded) {
            isResolving = true;
            continue;
          }
          currentValues = entry.values;
        }

        for (const rule of trigger.logicRules ?? []) {
          if (ruleHasDeletedValue(rule, currentValues)) {
            deletedByRuleId.add(rule.id);
          }
        }
      }

      return { deletedByRuleId, isResolving, hasUnverifiableList };
    },
    [listIds, triggers],
  );

  const { deletedByRuleId, isResolving, hasUnverifiableList } = useQueries({
    queries: listIds.map((listId) => getListOptionsQueryOptions({ listId, isAWA })),
    combine,
  });

  return { hasDeletedValues: deletedByRuleId.size > 0, deletedByRuleId, isResolving, hasUnverifiableList };
}
